import { useMemo } from 'react';
import { orderBy, where } from 'firebase/firestore';
import { useCollection } from 'core/hooks/useCollection';
import { IDeliveryRequestDao } from 'core/api/types/delivery-requests.interface';
import { AWAITING_ACTION } from 'core/constants/delivery-request-status';
import { DeliveryRequestsApiService } from 'core/api';

/**
 * The requests that have stopped and are waiting for a person.
 *
 * Asked of the database rather than counted in the browser. The screens load a
 * page at a time, so filtering a loaded page for the waiting ones cannot
 * report more than a page — and does not fail loudly when it starts to matter,
 * it just quietly reports a smaller number than the truth. The badge that is
 * meant to make someone look would read zero with a dozen waiting.
 *
 * Shared by the sidebar badge and the Review segment's count so the two can
 * never disagree about how much work is outstanding.
 */

/**
 * Enough to be a useful number without subscribing to an unbounded set.
 *
 * The waiting set is the one that is meant to be worked down, so it should
 * never approach this. Past it the count reads "99+", which says the same
 * thing a bigger exact number would.
 */
export const AWAITING_LIMIT = 99;

export const useAwaitingRequests = () => {
  const constraints = useMemo(
    () => [
      // Not the ones already marked: a request somebody has decided about is in
      // the queue, not waiting for a decision. Counting it here kept the badge
      // at the same number all the way through a review session, which is the
      // opposite of what a badge on work is for.
      where('marked', '==', false),
      where('status', 'in', AWAITING_ACTION),
      orderBy('created.at', 'desc'),
    ],
    [],
  );

  const { data } = useCollection<IDeliveryRequestDao>({
    apiService: DeliveryRequestsApiService,
    constraints,
    initialLimit: AWAITING_LIMIT,
  });

  const requests = data ?? [];
  return {
    requests,
    count: requests.length,
    /** True when there may be more than the subscription carries. */
    capped: requests.length >= AWAITING_LIMIT,
    label: requests.length >= AWAITING_LIMIT ? `${AWAITING_LIMIT}+` : String(requests.length),
  };
};
